// src/pages/TenantOnboard.jsx
import React, { useState, useEffect, useId, useMemo } from 'react';

export default function TenantOnboard({
  embedded = false,
  onDone,
  onCancel,
  autoCloseOnSuccess = false,
}) {
  const [shopName, setShopName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null); // { text, kind } | null

  const shopId = useId();
  const emailId = useId();
  const emailHelpId = useId();

  // Lightweight, practical email validator
  const isValidEmail = (email) => {
    const e = String(email).trim();
    // quick blocks
    if (!e || e.length > 254) return false;
    // basic shape local@domain.tld
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
    if (!re.test(e)) return false;
    // prevent consecutive dots and leading/trailing dots in local part
    const [local, domain] = e.split('@');
    if (!local || !domain) return false;
    if (local.startsWith('.') || local.endsWith('.') || local.includes('..')) return false;
    if (domain.startsWith('-') || domain.endsWith('-')) return false;
    if (domain.includes('..')) return false;
    return true;
  };

  const emailValid = useMemo(() => isValidEmail(ownerEmail), [ownerEmail]);
  const shopValid = useMemo(() => shopName.trim().length > 0, [shopName]);
  const formValid = emailValid && shopValid && !loading;

  function friendlyErrorFrom(resStatus, serverMsg) {
    const msg = String(serverMsg || '').toLowerCase();
    if (resStatus === 409 || msg.includes('already')) {
      return { kind: 'error', text: 'That shop / organisation name is already registered. Try a different name, or sign in if it’s yours.' };
    }
    if (resStatus === 400 || resStatus === 422) {
      return { kind: 'error', text: 'Please check the details and try again.' };
    }
    if (resStatus >= 500 && resStatus <= 599) {
      return { kind: 'warn', text: 'We couldn’t complete the setup right now. Please try again in a moment.' };
    }
    // Network / unexpected
    return { kind: 'warn', text: 'We’re having trouble reaching the server. Please check your connection and try again.' };
  }

  async function submit(e) {
    e.preventDefault();
    setEmailTouched(true); // force show email error if invalid
    if (!formValid) return;

    setLoading(true); setError(null); setResult(null);
    try {
      const payload = {
        shopName: shopName.trim(),
        ownerEmail: ownerEmail.trim().toLowerCase(),
      };
      const res = await fetch('/api/onboard', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      let data;
      try { data = await res.json(); } catch { data = {}; }

      if (!res.ok) {
        const fe = friendlyErrorFrom(res.status, data?.error);
        throw fe;
      }

      setResult(data.tenant);
    } catch (err) {
      // err may be our thrown {kind,text} OR a native Error
      if (err && err.text) setError(err);
      else setError(friendlyErrorFrom(undefined, err?.message));
    } finally {
      setLoading(false);
    }
  }

  // Auto-close after success (optional)
  useEffect(() => {
    if (embedded && autoCloseOnSuccess && result) {
      const t = setTimeout(() => onDone?.(), 1000);
      return () => clearTimeout(t);
    }
  }, [embedded, autoCloseOnSuccess, result, onDone]);

  return (
    <div className={embedded ? 'sk-onboard sk-onboard--embedded' : 'min-h-screen flex items-center justify-center p-6 sk-onboard'}>
      <div className={`card ${embedded ? 'card--compact' : ''}`}>
        {embedded ? (
          <p className="subtitle">An account will be created with login credentials.</p>
        ) : (
          <>
            <h1 className="title">Register your Shop / Organisation</h1>
            <p className="subtitle">An account will be set up for you with login credentials.</p>
          </>
        )}

        {!result && (
          <form onSubmit={submit} className="form" noValidate>
            <label className="label" htmlFor={shopId}>Shop / Organisation Register Name</label>
            <input
              id={shopId}
              className="input"
              value={shopName}
              onChange={(e) => setShopName(e.target.value)}
              placeholder="e.g. Anvi Electronics"
              required
              autoComplete="organization"
            />

            <label className="label" htmlFor={emailId}>Owner email</label>
            <input
              id={emailId}
              className={`input ${emailTouched && !emailValid ? 'input--invalid' : ''}`}
              type="email"
              inputMode="email"
              value={ownerEmail}
              onChange={(e) => setOwnerEmail(e.target.value)}
              onBlur={() => setEmailTouched(true)}
              placeholder="you@example.com"
              required
              autoComplete="email"
              aria-invalid={emailTouched && !emailValid ? 'true' : 'false'}
              aria-describedby={emailHelpId}
            />
            <div id={emailHelpId} className={`help ${emailTouched && !emailValid ? 'help--error' : ''}`}>
              {emailTouched && !emailValid ? 'Please enter a valid email address.' : 'We will send the temporary password to this email.'}
            </div>

            <div className="actions">
              <button disabled={!formValid} className="btn btn-primary" type="submit" title={!formValid ? 'Enter a shop name and a valid email' : undefined}>
                {loading ? 'Creating…' : 'Create Shop / Organisation'}
              </button>
              {embedded && (
                <button type="button" className="btn btn-ghost" onClick={onCancel}>
                  Cancel
                </button>
              )}
            </div>

            {error && (
              <div className={`balert ${error.kind === 'warn' ? 'balert-warn' : 'balert-error'}`} role="status" aria-live="polite">
                <div className="balert-icon" aria-hidden="true">{error.kind === 'warn' ? '⚠️' : '⛔'}</div>
                <div className="balert-text">
                  {error.text}
                  {error.kind !== 'warn' && (
                    <div className="balert-hint">Tip: choose a unique shop / organisation name. If this is your shop, <a href="/login" className="alink">sign in</a>.</div>
                  )}
                </div>
                <button className="balert-close" onClick={() => setError(null)} aria-label="Dismiss">✕</button>
              </div>
            )}
          </form>
        )}

        {result && (
          <div className="success">
            <h2 className="success-title">Registration Successful</h2>
            <p className="success-note">
              A temporary password has been emailed. Please change it after your first login.
            </p>

            <div className="kv">
              <div><span className="kv-k">Shop / Organisation Name:</span> <span className="kv-v">{result.slug}</span></div>
              <div><span className="kv-k">Login Email:</span> <span className="kv-v">{ownerEmail}</span></div>
              {result.tenantId && (
                <div><span className="kv-k">Shop / Organisation ID:</span> <span className="kv-v">{result.tenantId}</span></div>
              )}
            </div>

            <div className="actions">
              <button className="btn btn-primary" onClick={() => onDone?.()}>
                Done
              </button>
              {!embedded && (
                <a href="/login" className="btn btn-ghost">Back to Login</a>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Scoped styles */}
      <style>{`
        .sk-onboard {
          --bg-surface: var(--bg-elev, #ffffff);
          --bg-panel: var(--bg, #0f1222);
          --fg: var(--fg, #0b1220);
          --muted: var(--muted, #6b7280);
          --hairline: var(--hairline, #e5e7eb);
          --primary: var(--link, #2563eb);
          --primary-ink: #ffffff;
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          box-sizing: border-box;
        }
        .sk-onboard--embedded {
          min-height: auto;
          display: block;
          padding: 0;
        }
        .sk-onboard--embedded { color: var(--fg); }

        .card {
          width: 100%;
          max-width: 680px;
          background: var(--bg-surface);
          color: var(--fg);
          border: 1px solid var(--hairline);
          border-radius: 16px;
          box-shadow: 0 10px 30px rgba(2,6,23,.08);
          padding: 20px;
        }
        .card--compact { max-width: 640px; padding: 16px 18px; }

        .title { margin: 0 0 6px; font-size: 22px; line-height: 1.25; font-weight: 700; }
        .subtitle { margin: 0 0 16px; font-size: 14px; color: var(--muted); }

        .form { display: grid; grid-template-columns: 1fr; gap: 10px; }
        .label { font-size: 12px; color: var(--muted); }
        .input {
          height: 40px; border-radius: 10px; border: 1px solid var(--hairline);
          background: #fff; color: #0f172a; padding: 0 12px; outline: none;
          transition: border-color .15s ease, box-shadow .15s ease;
        }
        .input::placeholder { color: #9ca3af; }
        .input:focus {
          border-color: color-mix(in srgb, var(--primary) 70%, #93c5fd);
          box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary) 18%, transparent);
        }
        .input--invalid {
          border-color: #ef4444;
          box-shadow: 0 0 0 3px color-mix(in srgb, #ef4444 18%, transparent);
        }

        .help { font-size: 12px; margin-top: 2px; color: var(--muted); }
        .help--error { color: #b91c1c; }

        .actions { display: flex; gap: 10px; margin-top: 6px; flex-wrap: wrap; }
        .btn {
          height: 40px; border-radius: 10px; border: 1px solid var(--hairline);
          padding: 0 14px; font-weight: 600; cursor: pointer; background: #fff; color: #0f172a;
        }
        .btn:disabled { opacity: .6; cursor: not-allowed; }
        .btn-primary { background: var(--primary); color: var(--primary-ink); border-color: var(--primary); }
        .btn-primary:hover { filter: brightness(1.05); }
        .btn-ghost { background: transparent; }
        .btn-ghost:hover { background: rgba(2,6,23,.04); }

        /* New polished alert */
        .balert {
          display: grid;
          grid-template-columns: 24px 1fr auto;
          align-items: start;
          gap: 10px;
          margin-top: 10px;
          border-radius: 12px;
          padding: 12px 14px;
          border: 1px solid transparent;
          line-height: 1.45;
          white-space: normal;      /* Prevent weird per-word breaks */
          word-break: break-word;   /* Allow long tokens to wrap */
        }
        .balert-error {
          background: #fff1f2;      /* rose-50 */
          border-color: #fecdd3;    /* rose-200 */
          color: #9f1239;           /* rose-800 */
        }
        .balert-warn {
          background: #fffbeb;      /* amber-50 */
          border-color: #fde68a;    /* amber-300 */
          color: #92400e;           /* amber-800 */
        }
        .balert-icon { font-size: 18px; line-height: 1; margin-top: 2px; }
        .balert-text { font-size: 14px; }
        .balert-hint { margin-top: 6px; font-size: 12px; opacity: .9; }
        .balert-close {
          align-self: center;
          background: transparent; border: none; cursor: pointer;
          font-size: 16px; line-height: 1; opacity: .7;
        }
        .balert-close:hover { opacity: 1; }
        .alink { color: var(--primary); text-decoration: underline; }

        .success { margin-top: 8px; }
        .success-title { font-size: 16px; margin: 0 0 6px; font-weight: 600; }
        .success-note { font-size: 12px; color: var(--muted); margin: 0 0 10px; }

        .temp-pass { margin: 10px 0; border: 1px dashed var(--hairline);
          border-radius: 10px; padding: 10px 12px; background: #f9fafb; }
        .temp-pass-label { font-size: 12px; color: var(--muted); margin-bottom: 4px; }
        .temp-pass-value {
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
          font-size: 15px; letter-spacing: 0.02em; user-select: all;
        }

        .kv { display: grid; gap: 6px; margin-top: 10px; }
        .kv-k { color: var(--muted); margin-right: 6px; }
        .kv-v { font-weight: 500; }
      `}</style>
    </div>
  );
}
